import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { trackMagicLinkVerified } from "../lib/analytics.js";

type AuthError =
  | { kind: "expired" }
  | { kind: "missing" }
  | { kind: "network" }
  | { kind: "generic"; message: string };

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<AuthError | null>(null);
  // Guard: useEffect deps include `refresh`, whose identity changes on every
  // AuthProvider render. Without this ref we re-verify (and re-fire the
  // magic_link_verified analytics event) on every re-render — the variant
  // attribution metric would be massively over-counted.
  const verifiedOnceRef = useRef(false);

  const verify = useCallback(async () => {
    const token = params.get("token");
    if (!token) {
      setError({ kind: "missing" });
      return;
    }
    // Client-side preflight: if we can decode `exp` and it's already past,
    // skip the server roundtrip and show a specific message. The server is
    // still the trust boundary — every signature check happens there. If the
    // token is malformed or unparseable, we silently fall through to
    // verifyToken() and let the server speak.
    const expMs = decodeJwtExpiryMs(token);
    if (expMs !== null && expMs < Date.now()) {
      setError({ kind: "expired" });
      return;
    }
    setError(null);
    try {
      const { variant } = await api.verifyToken(token);
      trackMagicLinkVerified(variant);
      await refresh();
      const { family } = await api.myFamily();
      navigate(family ? "/calendar" : "/onboarding", { replace: true });
    } catch (err) {
      // ApiError = server reachable, signature/state mismatch — fall through.
      // Anything else (TypeError "Failed to fetch", DNS, offline) = network
      // problem we should let the user retry without re-clicking the email.
      if (err instanceof ApiError) {
        setError({ kind: "generic", message: err.message });
      } else {
        setError({ kind: "network" });
      }
    }
  }, [params, navigate, refresh]);

  useEffect(() => {
    if (verifiedOnceRef.current) return;
    verifiedOnceRef.current = true;
    void verify();
  }, [verify]);

  const onRetry = useCallback(() => {
    void verify();
  }, [verify]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        {error ? (
          <ErrorView error={error} onRetry={onRetry} />
        ) : (
          <p className="text-stone-600">Signing you in…</p>
        )}
      </div>
    </main>
  );
}

function ErrorView({ error, onRetry }: { error: AuthError; onRetry: () => void }) {
  if (error.kind === "network") {
    return (
      <>
        <h1 className="text-2xl font-semibold tracking-tight">Couldn't reach Inkling</h1>
        <p className="mt-3 text-stone-600">
          Check your connection and try again. Your sign-in link is still valid.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 inline-block rounded-lg bg-stone-900 text-white px-5 py-2 font-medium hover:bg-stone-800"
        >
          Try again
        </button>
      </>
    );
  }
  if (error.kind === "expired") {
    return (
      <>
        <h1 className="text-2xl font-semibold tracking-tight">This sign-in link has expired</h1>
        <p className="mt-3 text-stone-600">
          Magic links are good for 15 minutes. Request a new one and we'll email it right over.
        </p>
        <a
          href="/"
          className="mt-6 inline-block rounded-lg bg-stone-900 text-white px-5 py-2 font-medium"
        >
          Request a new link
        </a>
      </>
    );
  }
  if (error.kind === "missing") {
    return (
      <>
        <h1 className="text-2xl font-semibold tracking-tight">Couldn't sign you in</h1>
        <p className="mt-3 text-stone-600">Missing token. Open the link from your email again.</p>
        <a
          href="/"
          className="mt-6 inline-block rounded-lg bg-stone-900 text-white px-5 py-2 font-medium"
        >
          Try again
        </a>
      </>
    );
  }
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight">Couldn't sign you in</h1>
      <p className="mt-3 text-stone-600">{error.message}</p>
      <a
        href="/"
        className="mt-6 inline-block rounded-lg bg-stone-900 text-white px-5 py-2 font-medium"
      >
        Try again
      </a>
    </>
  );
}

/**
 * Decode the `exp` claim from a JWT (HS256, three dot-separated base64url
 * segments). Returns expiry in milliseconds since epoch, or null if the token
 * is unparseable. This is a UX preflight only — the server still verifies the
 * signature on every request.
 */
function decodeJwtExpiryMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
    const json = atob(padded + padding);
    const claims: unknown = JSON.parse(json);
    if (typeof claims !== "object" || claims === null) return null;
    const exp = (claims as { exp?: unknown }).exp;
    if (typeof exp !== "number" || !Number.isFinite(exp)) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}
